import { yupResolver } from '@hookform/resolvers/yup'
import { useDisabledPromoCodesList } from '@if/core'
import { compareAsc } from '@if/core/date'
import {
  EDocumentStatus,
  EDocumentType,
  EFinancingProgram,
  EPaymentFrequency,
  EPaymentMethod,
  EPaymentPlan,
  EPlanType,
  EProvince,
  EWorksheetStatus,
  getEPaymentMethodList,
} from '@if/core/types'
import { formatCurrency, formatDate, normalizeNumber } from '@if/core/utils'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import { Delete, ErrorOutline, Lock } from '@mui/icons-material'
import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, Stack, Typography, useTheme } from '@mui/material'
import {
  Breadcrumb,
  IconButtonWithTooltip,
  InputTextField,
  PageError,
  PageSpinner,
  SelectComponent,
} from '@src/components'
import HighEstimateDialog from '@src/components/HighEstimateDialog'
import SelectMerchantDialog from '@src/components/SelectMerchantDialog'
import MerchantPaymentInstructions from '@src/containers/ViewCreditApplicationPage/components/MerchantPaymentInstructions'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import {
  useMaxPaymentAmountWithAgentLeeway,
  useMaxPaymentError,
} from '@src/containers/ViewWorksheet/viewWorksheet-hooks'
import {
  canComputeFunding,
  getEmptyComputedFundingDto,
  getHighCostRateForProvince,
} from '@src/containers/ViewWorksheet/viewWorksheet-selectors'
import { useCompleteFinalizeHighEstimate } from '@src/data/api/personal-loan-worksheet/personal-loan-worksheet-api'
import { TRootState, useAppDispatch, useAppSelector } from '@src/data/store'
import { appActions, appSelectors } from '@src/data/store/AppStore'
import availableCreditEffects from '@src/data/store/AvailableCredit/available-credit-effects'
import { availableCreditSelectors } from '@src/data/store/AvailableCredit/available-credit-store'
import { creditSelectors } from '@src/data/store/CreditApplication'
import { documentSelectors } from '@src/data/store/Document'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { fullCreditApplicationEffects } from '@src/data/store/FullCreditApplication'
import { messageEffects, messageSelectors } from '@src/data/store/Message'
import { personalLoanWorksheetEffects } from '@src/data/store/PersonalLoanWorksheet'
import {
  personalLoanWorksheetActions,
  personalLoanWorksheetSelectors,
} from '@src/data/store/PersonalLoanWorksheet/personal-loan-worksheet-store'
import { userSelectors } from '@src/data/store/UserStore'
import {
  buildEditPersonalLoanWorksheetDtoSchema,
  Constants,
  CreditApplicationMessage,
  EditPersonalLoanWorksheetDto,
  EditWorksheetPaymentSplitDto,
  EMerchantIds,
  Merchant,
  SelectValueListItem,
} from '@src/data/types'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { MerchantPayment } from '@src/data/types/MerchantPayment'
import { getApiClient } from '@src/services/api-client'
import { reportErrorToConsole } from '@src/services/error-logger'
import { formatPaymentPlanForSummary } from '@src/services/Formatter'
import { debounce, sumBy } from 'lodash-es'
import { nanoid } from 'nanoid'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import AvailableCreditTooltip from '../../../components/AvailableCreditTooltip'
import allApis from '../../../data/api'
import {
  aprThresholds,
  useAprTooHigh,
  useFirstPaymentDateOptions,
  usePaymentPlanList,
  usePossibleTerms,
} from '../editWorksheet-hooks'
const PAY_CUSTOMER_IDS: string[] = [EMerchantIds.PayCustomerByBankTransfer, EMerchantIds.PayCustomerByCheque]

const apiClient = getApiClient()

const EditPersonalLoanWorksheetPage = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()

  const creditApplication = useAppSelector(creditSelectors.getCurrent)
  const financingConfig = useAppSelector((state: TRootState) =>
    appSelectors.getFinancingConfig(state, creditApplication?.financingProgramId),
  )

  const worksheetYupResolver = useMemo(
    () => yupResolver(buildEditPersonalLoanWorksheetDtoSchema(financingConfig)),
    [financingConfig],
  )

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors },
  } = useForm<EditPersonalLoanWorksheetDto>({
    mode: 'onBlur',
    defaultValues: {} as EditPersonalLoanWorksheetDto,
    resolver: worksheetYupResolver,
    context: { loanPurposeId: creditApplication?.loanPurposeId },
  })

  const dispatch = useAppDispatch()
  const dispatchEffect = useSideEffect()
  const theme = useTheme()
  const availableCreditData = useAppSelector(availableCreditSelectors.get)
  const availableCredit = useAppSelector(availableCreditSelectors.getAvailableCredit)
  const creditInProgress = useAppSelector(availableCreditSelectors.getCreditInProgress)
  const isLoading = useAppSelector(personalLoanWorksheetSelectors.isSavingPersonalLoanWorksheet)
  const isLoadingCreditApplication = useAppSelector(creditSelectors.isLoadingCreditApplication)
  const isComputingFunding = useAppSelector(personalLoanWorksheetSelectors.isComputePersonalLoanFunding)
  const error = useAppSelector(appSelectors.getBusinessError)
  const merchant = useAppSelector(creditSelectors.getMerchant)
  const hasCVT = useAppSelector(creditSelectors.hasCVT)
  const personalLoanWorksheet = useAppSelector(personalLoanWorksheetSelectors.getCurrent)
  const computedInfo = useAppSelector(personalLoanWorksheetSelectors.getComputedInfo)
  const totalFees = useAppSelector(personalLoanWorksheetSelectors.getTotalfees)
  const totalInsurance = useAppSelector(personalLoanWorksheetSelectors.getTotalInsurance)
  const disabledPromoList = useDisabledPromoCodesList(
    merchant?.planPromoRules,
    creditApplication?.normsCreditDecision,
    creditApplication?.financingProgramId ?? EFinancingProgram.Personal,
  )

 const useUnlockElitePromoForQuebecApplicants = useSelector(
   appSelectors.getFeatureFlags,
 ).useUnlockElitePromoForQuebecApplicants
  const merchantPaymentPlans = useMemo(() => {
    if (
      !useUnlockElitePromoForQuebecApplicants &&
      creditApplication?.applicant.currentAddress.stateIso === EProvince.Quebec
    ) {
      return merchant?.paymentPlans.filter((plan) => !plan.supportsChargeback) ?? []
    }
    return merchant?.paymentPlans ?? []
  }, [
    useUnlockElitePromoForQuebecApplicants,
    creditApplication?.applicant.currentAddress.stateIso,
    merchant?.paymentPlans,
  ])
  const paymentPlanList = usePaymentPlanList(
    creditApplication?.createdOn,
    merchantPaymentPlans,
    disabledPromoList,
    personalLoanWorksheet?.paymentPlanId,
  )

  const aprTooHigh = useAprTooHigh(computedInfo, creditApplication)
  const privateMessages = useAppSelector(messageSelectors.getPrivateMessages)
  const user = useAppSelector(userSelectors.getUser)
  const firstPaymentOn = watch('firstPaymentOn')
  const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
  const selectedCompany = useAppSelector(userSelectors.selectedCompany)
  const [isMerchantDialogOpen, setIsMerchantDialogOpen] = useState<boolean>(false)
  const [isVariableInterest, setIsVariableInterest] = useState(false)

  const possibleTerms = usePossibleTerms(
    creditApplication?.finalCreditDecision.maxTermDuration,
    isVariableInterest,
    financingConfig,
    amountRequested,
  )
  const provinceConfigs = useAppSelector(appSelectors.getProvinceConfigurations)
  const [isLoadingMerchants, setIsLoadingMerchants] = useState(true)
  const [paymentMerchantsByIds, setPaymentMerchantsByIds] = useState<Record<string, Merchant>>({})
  const [highEstimateShouldDisableFields, setHighEstimateShouldDisableFields] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
  const currentDateAsEasternTimeZone = new Date(easternTime)
  const [completeFinalizeHighEstimate, isPending] = useCompleteFinalizeHighEstimate()
  const docs = useAppSelector(documentSelectors.getDocumentList)
  useEffect(() => {
    setHighEstimateShouldDisableFields(
      (personalLoanWorksheet?.isHighEstimate && merchant?.canAcceptHighEstimate === true && hasCVT) ?? false,
    )
  }, [personalLoanWorksheet, merchant, hasCVT])

  // featureSwitch
  const useHighEstimate = useSelector(appSelectors.getFeatureFlags).useHighEstimate

  useEffect(() => {
    if (amountRequested) trigger('term').catch(reportErrorToConsole)
  }, [amountRequested, trigger])

  // dynamic values
  const payments = watch('merchantPayments')
  const paymentFrequency = watch('paymentFrequency')
  const paymentPlanId = watch('paymentPlanId') as EPaymentPlan
  const deliveryOn = watch('deliveryOn')
  const term = watch('term')
  const includeInsurance = watch('includeInsurance')
  const isHighEstimate = watch('isHighEstimate')
  const isNFl =
    creditApplication?.applicant.currentAddress.stateIso &&
    creditApplication.applicant.currentAddress.stateIso === EProvince.Newfoundland

  const highCostRateForProvince = getHighCostRateForProvince(
    provinceConfigs,
    creditApplication?.applicant.currentAddress.stateIso,
  )
  const maxPaymentAmount = useMaxPaymentAmountWithAgentLeeway(
    paymentPlanId,
    creditApplication?.finalCreditDecision?.maxPmtAmount ?? 0,
  )

  const maxPaymentForFrequencyError = useMaxPaymentError(
    personalLoanWorksheet?.paymentFrequency,
    computedInfo?.paymentForFrequency,
    maxPaymentAmount,
    dispatch,
    Constants.financingProgramConfigs[EFinancingProgram.Personal].validateMaxPaymentAmountInWorksheet,
  )
  const totalObligation = amountRequested + (computedInfo?.totalInterestAmount ?? 0) + totalFees + totalInsurance
  const planName = creditApplication?.normsCreditDecision?.planName ?? EPlanType.Default

  const setPayments = useCallback(
    (merchantPayment: MerchantPayment[]) => {
      setValue('merchantPayments', merchantPayment)
    },
    [setValue],
  )

  const possiblePaymentMethods = useMemo(
    () => getEPaymentMethodList().map((x) => ({ label: t(`EPaymentMethod.${x}`, { ns: 'enum' }), value: x })),
    [t],
  )

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.Personal}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const selectedPlan = useMemo(
    () => merchant?.paymentPlans?.find((p) => p.id === paymentPlanId),
    [merchant?.paymentPlans, paymentPlanId],
  )

  useEffect(() => {
    if (creditApplication?.id) {
      dispatchEffect(
        availableCreditEffects.getAvailableCredit(creditApplication.id, creditApplication.financingProgramId),
      ).catch(reportErrorToConsole)
    }
  }, [creditApplication, dispatchEffect])

  // reset form on worksheet changed
  useEffect(() => {
    if (personalLoanWorksheet && merchant) {
      reset(personalLoanWorksheet as EditPersonalLoanWorksheetDto)
      setPayments(personalLoanWorksheet.merchantPayments ?? [])
      setValue('firstPaymentOn', personalLoanWorksheet.firstPaymentOn ?? formatDate(new Date()))

      const removedMerchantIds = [...PAY_CUSTOMER_IDS, merchant.id]
      const merchantIdsToFetch = personalLoanWorksheet
        .merchantPayments!.filter((p) => !removedMerchantIds.includes(p.merchantId))
        .map((p) => p.merchantId)

      const newPaymentMechantsByIds = {
        [merchant.id]: merchant,
      }
      if (merchantIdsToFetch.length > 0 && apiClient) {
        const promises = merchantIdsToFetch.map((merchantId) =>
          allApis.config.getMerchantById(apiClient, { id: merchantId }),
        )
        Promise.all(promises)
          .then((results) => {
            results.forEach((m) => {
              newPaymentMechantsByIds[m.id] = m
            })
            setPaymentMerchantsByIds(newPaymentMechantsByIds)
            setIsLoadingMerchants(false)
          })
          .catch(reportErrorToConsole)
      } else {
        setPaymentMerchantsByIds(newPaymentMechantsByIds)
        setIsLoadingMerchants(false)
      }
    }
  }, [personalLoanWorksheet, merchant, reset, setPayments, setValue])

  const computeFunding = useCallback(
    (computeDto: FundingInfoDto) => {
      return dispatchEffect(personalLoanWorksheetEffects.computePersonalLoanFunding(computeDto))
    },
    [dispatchEffect],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceComputeFunding = useCallback(debounce(computeFunding, 500), [computeFunding])
  // recompute funding
  useEffect(() => {
    const interestRate = creditApplication?.finalCreditDecision.interestRate ?? 0
    const hasCoApplicant = creditApplication?.coApplicant !== null
    const stateIso = creditApplication?.applicant.currentAddress.stateIso
    const requestId = nanoid()
    if (
      amountRequested >= financingConfig.minimumLoanAmount &&
      paymentFrequency &&
      term &&
      deliveryOn &&
      paymentPlanId &&
      firstPaymentOn &&
      stateIso &&
      personalLoanWorksheet &&
      merchant
    ) {
      const computeDto: FundingInfoDto = {
        financingProgramId: creditApplication?.financingProgramId,
        amountRequested,
        includeInsurance,
        insuranceCompanyId: personalLoanWorksheet.insuranceCompanyId,
        paymentFrequency,
        paymentPlanId,
        term,
        interestRate,
        hasCoApplicant,
        stateIso,
        deliveryOn,
        firstPaymentOn,
        requestId,
        creditApplicationId: creditApplication.id,
        merchantId: merchant.id,
      }

      if (canComputeFunding(computeDto, financingConfig.minimumLoanAmount))
        debounceComputeFunding(computeDto)?.catch(reportErrorToConsole)
      else {
        const emptyComputedDto = getEmptyComputedFundingDto()

        dispatch(personalLoanWorksheetActions.setFundingComputed(emptyComputedDto))
      }
    }
  }, [
    amountRequested,
    paymentFrequency,
    paymentPlanId,
    deliveryOn,
    firstPaymentOn,
    term,
    includeInsurance,
    creditApplication,
    dispatch,
    id,
    debounceComputeFunding,
    merchant?.id,
    merchant,
    personalLoanWorksheet,
    financingConfig.minimumLoanAmount,
  ])

  useEffect(() => {
    const isVariable = Boolean(paymentPlanId && paymentPlanId !== EPaymentPlan.RegularDailyInterests)

    if (isVariable && selectedPlan) {
      setValue('term', selectedPlan.loanTerm)
      if (payments) setPayments(payments.filter((p) => !PAY_CUSTOMER_IDS.includes(p.merchantId)))
    }

    setIsVariableInterest(isVariable)
  }, [paymentPlanId, payments, selectedPlan, selectedPlan?.loanTerm, setPayments, setValue])

  //La raison pour laquel on ne permet pas d'avoir un High Estimate et une promo en même temps
  //sur une demande et que la plupart des promo ont des frais ajouté au marchand.
  // Donc, si on fait une partie du paiement directement au client (principe du High Estimate)
  //le marchand ne voudra pas payer des frais pour que l'argent soit versé directement au consomateur.
  useEffect(() => {
    if (paymentPlanId !== EPaymentPlan.RegularDailyInterests && isHighEstimate) {
      setValue('isHighEstimate', false)
    }
  }, [paymentPlanId, isHighEstimate, setValue])
  const getMerchantFees = () => {
    if (selectedPlan) return formatCurrency((selectedPlan.merchantFeeRate / 100) * amountRequested)
    return ''
  }
  // recompute first payment options and date
  const { firstPaymentDateOptions } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn,
    paymentFrequency,
    isHighEstimate: isHighEstimate,
    setValue,
    getValues,
  })

  const getMerchantName = useCallback(
    (mId: string) => {
      if (creditApplication && PAY_CUSTOMER_IDS.some((n) => n === mId)) {
        return `${creditApplication.applicant.firstName} ${creditApplication.applicant.lastName}`
      }
      return paymentMerchantsByIds[mId]?.name ?? ''
    },
    [paymentMerchantsByIds, creditApplication],
  )

  const handleMerchantsSelected = useCallback(
    (newMerchants: Merchant[]) => {
      setPaymentMerchantsByIds((oldPaymentMerchantsByIds) => {
        const newPaymentMerchants = { ...oldPaymentMerchantsByIds }
        newMerchants.forEach((m) => {
          newPaymentMerchants[m.id] = m
        })
        return newPaymentMerchants
      })

      const currentPayments: MerchantPayment[] = [...payments]

      newMerchants.forEach((merch: Merchant) => {
        const existing = currentPayments.find((p) => p.merchantId === merch.id)

        if (!existing) {
          currentPayments.push({
            merchantId: merch.id,
            amount: 0,
            paymentMethod: merch.defaultPaymentMethod ?? EPaymentMethod.Check,
          })
        }
      })

      setPayments(currentPayments)

      setIsMerchantDialogOpen(false)
    },
    [payments, setPayments],
  )

  const addBorrower = () => {
    const currentPayments: MerchantPayment[] = [...payments]
    const existing = currentPayments.find((p) => PAY_CUSTOMER_IDS.some((n) => n === p.merchantId))
    if (!existing) {
      currentPayments.push({
        merchantId: EMerchantIds.PayCustomerByBankTransfer,
        amount: 0,
        paymentMethod: EPaymentMethod.BankTransfer,
      })
    }
    setPayments(currentPayments)
  }

  const onDeleteMerchantPayment = (payment: MerchantPayment) => {
    const newPayments = payments.filter((p) => p.merchantId !== payment.merchantId)

    setPayments(newPayments)
  }

  const onMerchantPaymentAmountEdited = (newAmount: string, payment: MerchantPayment) => {
    const paymentIndex = payments.indexOf(payment)
    const tempPayments: MerchantPayment[] = [...payments]
    const normalizedAmount = normalizeNumber(newAmount)

    tempPayments[paymentIndex] = {
      merchantId: payment.merchantId,
      amount: Number(normalizedAmount),
      paymentMethod: payment.paymentMethod,
    }

    setPayments(tempPayments)
  }

  const onMerchantPaymentMethodEdited = (event: ChangeEvent<HTMLInputElement>, payment: MerchantPayment) => {
    const paymentIndex = payments.indexOf(payment)
    const tempPayments: MerchantPayment[] = [...payments]
    let paymentMerchantId = payment.merchantId
    if (PAY_CUSTOMER_IDS.some((n) => n === payment.merchantId)) {
      if ((event.target.value as EPaymentMethod) === EPaymentMethod.BankTransfer) {
        paymentMerchantId = EMerchantIds.PayCustomerByBankTransfer
      } else {
        paymentMerchantId = EMerchantIds.PayCustomerByCheque
      }
    }
    tempPayments[paymentIndex] = {
      merchantId: paymentMerchantId,
      amount: payment.amount,
      paymentMethod: event.target.value as EPaymentMethod,
    }

    setPayments(tempPayments)
  }
  const closeConfirmDialog = useCallback(() => {
    setIsDialogOpen(false)
  }, [])
  const closeMerchantDialog = useCallback(() => {
    setIsMerchantDialogOpen(false)
  }, [])
  const handleSave = async () => {
    if (isHighEstimate) {
      setIsDialogOpen(true)
    } else {
      const formData = getValues()
      await onFormSubmitted(formData)
    }
  }
  const onFormSubmitted = async (data: EditPersonalLoanWorksheetDto) => {
    setIsSubmitting(true)
    if (isDialogOpen) {
      setIsDialogOpen(false)
    }
    const expiry = new Date(creditApplication!.expiresOn)
    const hardHitReport = creditApplication!.applicant.hardHitReport || creditApplication!.coApplicant?.hardHitReport
    const receivedOnPlus90Days = new Date(hardHitReport.receivedOn)
    receivedOnPlus90Days.setDate(receivedOnPlus90Days.getDate() + 90)
    const totalPayments = normalizeNumber(sumBy(payments, 'amount')?.toString()) ?? 0
    const mainPayment = payments.find((p) => p.merchantId === merchant?.id)
    const deliveryOnDate = new Date(`${getValues('deliveryOn')}T20:00:00`) // 20PM Local Time
    const deliveryOnUTC = new Date(`${getValues('deliveryOn')}T00:00:00`)
    const deliveryOnDateAsEasternTimeZone = new Date(
      deliveryOnDate.toLocaleString('en-US', { timeZone: 'America/Toronto' }),
    )
    const baseActivationDateAsEasternTimeZone = new Date(
      currentDateAsEasternTimeZone.getFullYear(),
      currentDateAsEasternTimeZone.getMonth(),
      currentDateAsEasternTimeZone.getDate(),
      0,
      0,
      0,
    )
    try {
      if (
        isVariableInterest &&
        (!mainPayment ||
          (selectedPlan && mainPayment && (selectedPlan.merchantFeeRate / 100) * amountRequested > mainPayment.amount))
      )
        dispatch(
          appActions.setBusinessErrors([
            {
              message: t('worksheetCommon.merchantMustHaveFees', { merchantFee: getMerchantFees() }),
            },
          ]),
        )
      else if (
        isNFl
          ? computedInfo.effectiveRate > highCostRateForProvince
          : computedInfo.effectiveRate > financingConfig.effectiveRateThreshold
      ) {
        dispatch(
          appActions.setBusinessErrors([
            {
              message: t('worksheetCommon.effectiveRateTooHigh', '', {
                effectiveRate: computedInfo.effectiveRate,
                maxRate: isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold,
              }),
            },
          ]),
        )
      } else if (compareAsc(deliveryOnDateAsEasternTimeZone, baseActivationDateAsEasternTimeZone) === -1) {
        dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.activationDateInPast' }]))
      } else if (amountRequested > availableCredit) {
        dispatch(
          appActions.setBusinessErrors([{ message: 'worksheetCommon.amountRequestedHigherThanAvailableCredit' }]),
        )
      } else if (compareAsc(deliveryOnUTC, receivedOnPlus90Days) === 1)
        dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.activationDateReport90Days' }]))
      else if (compareAsc(deliveryOnUTC, expiry) === 1)
        dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.deliveryOnEarlierExpiration' }]))
      else if (totalPayments !== amountRequested) {
        dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.merchantPaymentTotalMismatch' }]))
      } else {
        data.amountRequested = amountRequested
        data.status = EWorksheetStatus.Active
        data.merchantPayments = payments
        await dispatchEffect(personalLoanWorksheetEffects.update(data)).then(() =>
          dispatchEffect(fullCreditApplicationEffects.getById(creditApplication!.id, EFinancingProgram.Personal)).then(
            () =>
              navigate(`/Applications/${EFinancingProgram.Personal}/${creditApplication!.id}/view`, { replace: true }),
          ),
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const finalizeHighEstimate = async () => {
    const data: EditWorksheetPaymentSplitDto = {
      merchantPayments: payments,
      creditApplicationId: creditApplication!.id,
    }
    setIsSubmitting(true)
    try {
      const totalPayments = normalizeNumber(sumBy(payments, 'amount')?.toString()) ?? 0
      if (totalPayments !== amountRequested) {
        dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.merchantPaymentTotalMismatch' }]))
      }
      if (!personalLoanWorksheet?.isHighEstimate)
        dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.notHighEstimate' }]))
      if (!hasCVT) dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.optionNotAvailable' }]))
      if (!docs.find((doc) => doc.typeId === EDocumentType.ClinicInvoice && doc.status == EDocumentStatus.Approved))
        dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.invoiceNotApproved' }]))
      else {
        await completeFinalizeHighEstimate({ dto: data, creditApplicationId: creditApplication!.id }).then(() =>
          navigate(`/Applications/${EFinancingProgram.Personal}/${creditApplication!.id}/view`, { replace: true }),
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const getFilteredPaymentMethods = (payment: MerchantPayment) => {
    if (PAY_CUSTOMER_IDS.some((n) => n === payment.merchantId)) {
      return possiblePaymentMethods.filter((m) => m.value !== EPaymentMethod.Wire && m.value !== EPaymentMethod.DEFT)
    }
    return possiblePaymentMethods.filter((m) => m.value !== EPaymentMethod.BankTransfer)
  }

  const isPaymentMethodDisabled = (payment: MerchantPayment) => {
    // disabled for the time being. ALIS does not support custom merchant methods.
    // remove this disabling function once ALIS has support.
    if (PAY_CUSTOMER_IDS.includes(payment.merchantId)) return false
    return payment.paymentMethod === paymentMerchantsByIds[payment.merchantId].defaultPaymentMethod
  }

  const handleDeletingPrivateMessage = (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      return dispatchEffect(messageEffects.deleteMessage(message, creditApplication.financingProgramId))
    }
    return null
  }

  return (
    <div>
      <PageSpinner withBackdrop isLoading={(isLoading && isLoadingCreditApplication) || isSubmitting || isPending} />

      {!isLoading && (
        <Stack>
          <PageError errors={error} />
          {creditApplication && aprTooHigh && (
            <Alert severity="error">
              {t('common.errors.aprTooHigh', '', {
                threshold: creditApplication.applicant.currentAddress.stateIso
                  ? aprThresholds[creditApplication.applicant.currentAddress.stateIso]
                  : '',
                stateIso: creditApplication.applicant.currentAddress.stateIso
                  ? t(`EProvince.${creditApplication.applicant.currentAddress.stateIso}`, { ns: 'enum' })
                  : '',
              })}
            </Alert>
          )}
        </Stack>
      )}
      <Breadcrumb trees={breadCrumbs} />
      <form onSubmit={handleSubmit(onFormSubmitted, reportErrorToConsole)}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <InputTextField
            value={t(`EPlanType.${planName}`, { ns: 'enum' })}
            label={t('worksheetCommon.program')}
            disabled
          />
          <InputTextField disabled value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0} />
          <AvailableCreditTooltip availableCreditData={availableCreditData}>
            <div style={{ position: 'relative' }}>
              <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
              {creditInProgress !== 0 && (
                <ErrorOutline
                  sx={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.palette.error.main,
                    fontSize: '1.5rem',
                  }}
                />
              )}
            </div>
          </AvailableCreditTooltip>
          <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />

          {!highEstimateShouldDisableFields && (
            <div style={{ marginTop: '1%' }}>
              <Button
                variant="contained"
                disabled={
                  aprTooHigh || isComputingFunding || !computedInfo || isLoading || maxPaymentForFrequencyError !== null
                }
                sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'left' }}
                onClick={handleSave}
              >
                {t('common.save')}
              </Button>
            </div>
          )}
          {highEstimateShouldDisableFields && (
            <div>
              <Button
                variant="contained"
                disabled={aprTooHigh || isComputingFunding || !computedInfo || isLoading}
                onClick={() => setHighEstimateShouldDisableFields(false)}
                sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'left' }}
                startIcon={<Lock />}
              >
                <div>
                  <Typography variant="body1" component="div">
                    {t('worksheetCommon.unlockToModify')}
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ fontSize: '0.75rem' }}>
                    {t('worksheetCommon.willCancelContract')}
                  </Typography>
                </div>
              </Button>
            </div>
          )}
        </Stack>
        {creditApplication && (
          <PrivateMessage
            messages={privateMessages}
            handleDeletingPrivateMessage={handleDeletingPrivateMessage}
            currentUser={user}
            creditAppCreationDate={creditApplication.createdOn}
          />
        )}
        {merchant && <MerchantPaymentInstructions merchant={merchant} />}
        <Grid container marginTop={5}>
          <Grid size={8}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <InputTextField
                  slotProps={{
                    input: {
                      endAdornment: '$',
                    },
                  }}
                  {...register('amountRequested')}
                  error={errors?.amountRequested}
                  label={t('worksheetCommon.amountRequested')}
                  disabled={highEstimateShouldDisableFields}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <ControlledStringDateOnlyPicker
                  name="deliveryOn"
                  control={control}
                  label={t('worksheetCommon.activationDate')}
                  disablePast
                  minDate={currentDateAsEasternTimeZone}
                  disabled={creditApplication?.editLocked || highEstimateShouldDisableFields}
                  fullWidth
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={firstPaymentDateOptions.map((item) => {
                    const data: SelectValueListItem = {
                      label: formatDate(item),
                      value: formatDate(item),
                    }
                    return data
                  })}
                  label={t('worksheetCommon.firstPaymentDate')}
                  {...register('firstPaymentOn')}
                  error={errors?.firstPaymentOn}
                  value={formatDate(firstPaymentOn)}
                  disabled={highEstimateShouldDisableFields}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={paymentPlanList}
                  label={t('worksheet.paymentPlan')}
                  {...register('paymentPlanId')}
                  error={errors?.paymentPlanId}
                  disabled={highEstimateShouldDisableFields}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={[{ label: t('EPaymentFrequency.monthly', { ns: 'enum' }), value: EPaymentFrequency.Monthly }]}
                  label={t('worksheet.paymentFrequency')}
                  {...register('paymentFrequency')}
                  error={errors?.paymentFrequency}
                  disabled={highEstimateShouldDisableFields}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  disabled={isVariableInterest || highEstimateShouldDisableFields}
                  items={possibleTerms.map((item) => ({
                    label: `${item} ${t('worksheet.month')}`,
                    value: item,
                  }))}
                  label={t('worksheet.term')}
                  {...register('term')}
                  error={errors?.term}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <FormControlLabel
                  {...register('includeInsurance')}
                  control={<Checkbox defaultChecked={personalLoanWorksheet?.includeInsurance} />}
                  label={t('worksheetCommon.addProtectionPlan')}
                  disabled={highEstimateShouldDisableFields}
                />
              </Grid>

              <Grid size={12}>
                <Box
                  width="100%"
                  sx={{
                    border: 1,
                    padding: 2,
                    marginTop: 2,
                    borderColor: theme.palette.divider,
                    borderRadius: 1,
                  }}
                >
                  <Grid container marginBottom={4} justifyContent="space-between" alignItems="center" display="flex">
                    {useHighEstimate && merchant?.canAcceptHighEstimate && (
                      <Grid>
                        <Controller
                          name="isHighEstimate"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <FormControlLabel
                              onChange={(_, checked) => onChange(checked)}
                              checked={paymentPlanId === EPaymentPlan.RegularDailyInterests ? value : false}
                              disabled={paymentPlanId !== EPaymentPlan.RegularDailyInterests}
                              control={<Checkbox defaultChecked={personalLoanWorksheet?.isHighEstimate} />}
                              label={t('worksheetCommon.checkIsHighEstimate')}
                            />
                          )}
                        />
                      </Grid>
                    )}
                    <Grid ml="auto">
                      <Stack direction="row" spacing={1}>
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => addBorrower()}
                          disabled={
                            isVariableInterest ||
                            payments?.some((p) => PAY_CUSTOMER_IDS.some((n) => n === p.merchantId)) ||
                            !merchant?.canPayBorrowers ||
                            merchant?.forcePaymentToBorrower
                          }
                        >
                          {t('worksheetCommon.addBorrower')}
                        </Button>
                        <Button
                          color="secondary"
                          variant="contained"
                          disabled={merchant?.forcePaymentToBorrower}
                          onClick={() => setIsMerchantDialogOpen(true)}
                        >
                          {t('worksheetCommon.addMerchantPayment')}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>

                  {payments &&
                    !isLoadingMerchants &&
                    payments.map((p, index) => (
                      <Stack key={p.merchantId} direction="row" justifyContent="space-between" spacing={2}>
                        <Typography sx={{ width: '100%', marginTop: '1rem' }}>
                          {getMerchantName(p.merchantId)}
                        </Typography>
                        <SelectComponent
                          disabled={isPaymentMethodDisabled(p)}
                          items={getFilteredPaymentMethods(p)}
                          label={t('worksheetCommon.paymentMethod')}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => onMerchantPaymentMethodEdited(e, p)}
                          value={p.paymentMethod}
                        />
                        <InputTextField
                          {...register(`merchantPayments.${index}.amount`)}
                          sx={{ width: '30rem' }}
                          label={t('worksheetCommon.paymentAmount')}
                          slotProps={{
                            input: {
                              endAdornment: '$',
                            },
                          }}
                          onBlur={(content) => onMerchantPaymentAmountEdited(content.target.value, p)}
                        />
                        <div style={{ marginTop: '1%' }}>
                          <IconButtonWithTooltip
                            disabled={isVariableInterest && p.merchantId === merchant?.id}
                            onClick={() => onDeleteMerchantPayment(p)}
                            tooltip="delete"
                            color="primary"
                            icon={<Delete />}
                          />
                        </div>
                      </Stack>
                    ))}
                  {useHighEstimate && merchant?.canAcceptHighEstimate && isHighEstimate && (
                    <Grid container justifyContent="center" mt={4}>
                      <Grid>
                        <Button
                          color="success"
                          variant="contained"
                          onClick={async () => {
                            await finalizeHighEstimate()
                          }}
                        >
                          {t('worksheetCommon.finalizeHighEstimateWorksheet')}
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={4}>
            <Box
              sx={{
                border: 1,
                padding: 2,
                marginLeft: 5,
                paddingBottom: 10,
              }}
            >
              <Stack direction="column" spacing={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('worksheetCommon.fundingSummary')} :
                </Typography>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.loanAmount')} :</Typography>
                  <Typography>{`${formatCurrency(getValues('amountRequested'))}`}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>
                    {`${t('worksheetCommon.interest')} (${
                      isVariableInterest
                        ? formatPaymentPlanForSummary(selectedPlan)
                        : `${creditApplication?.finalCreditDecision?.interestRate}%`
                    })`}
                    :{' '}
                  </Typography>
                  <Typography>{formatCurrency(computedInfo?.totalInterestAmount)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{`${t('worksheetCommon.financeFee')} (${computedInfo?.lenderFeeRate}%)`}:</Typography>
                  <Typography>{formatCurrency(totalFees)}</Typography>
                </Stack>

                {isVariableInterest && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>
                      {`${t('worksheetCommon.merchantFee')} (${selectedPlan?.merchantFeeRate}%)`} :
                    </Typography>
                    <Typography>{getMerchantFees()}</Typography>
                  </Stack>
                )}

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.protectionPlan')} :</Typography>
                  <Typography>{formatCurrency(totalInsurance)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.totalObligation')} :</Typography>
                  <Typography>{formatCurrency(totalObligation)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.annualPercentageRate')} :</Typography>
                  <Typography>{computedInfo.effectiveRate} %</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('worksheetCommon.payment')}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.maxPmtAmount')} :</Typography>
                  <Typography>{formatCurrency(creditApplication?.finalCreditDecision.maxPmtAmount)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.paymentFrequency')} :</Typography>
                  <Typography>
                    {personalLoanWorksheet?.paymentFrequency
                      ? t(`EPaymentFrequency.${personalLoanWorksheet?.paymentFrequency}`, { ns: 'enum' })
                      : t('EPaymentFrequency.monthly', { ns: 'enum' })}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.frequencyAmount')} :</Typography>
                  <Typography>{formatCurrency(computedInfo?.paymentForFrequency)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.firstPaymentDate')} :</Typography>
                  <Typography>{formatDate(firstPaymentOn)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                  <Typography fontWeight="bold">{t('worksheetCommon.amountToBePaid')} :</Typography>
                  <Typography>{formatCurrency(getValues('amountRequested') ?? 0)}</Typography>
                </Stack>
              </Stack>
            </Box>
          </Grid>
        </Grid>
        <HighEstimateDialog
          open={isDialogOpen}
          title={t('worksheetCommon.saveFinalizeHighEstimateTitle')}
          onConfirm={handleSubmit(onFormSubmitted, reportErrorToConsole)}
          onCancel={closeConfirmDialog}
          text={t('worksheetCommon.saveFinalizeHighEstimateMessage')}
          onChange={(value: boolean) => setValue('highEstimateExplainedToClient', value)}
        />
      </form>
      <SelectMerchantDialog
        open={isMerchantDialogOpen}
        title={t('worksheetCommon.addMerchantPayment')}
        onConfirm={handleMerchantsSelected}
        onCancel={closeMerchantDialog}
        allowMultiple
        financingProgramId={EFinancingProgram.Personal}
        label={t('taskManager.merchantName')}
        financingCompanyId={selectedCompany}
      />
    </div>
  )
}
export default EditPersonalLoanWorksheetPage
